import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  MessageSquare,
  Settings,
  LogOut,
  Users,
  Plus,
  ShieldCheck,
} from 'lucide-react';
import { VoiceChannelList, VoiceStatusIndicator } from '../VoiceChannels';
import { DesktopDownloadButton } from '../DesktopDownloadButton';
import { LocalVerifiedBadge } from '../LocalVerifiedBadge';
import { useSystemStatus } from '../../hooks/useSystemStatus';

export const Sidebar = ({
  activeTab,
  setActiveTab,
  onNewChat,
  onSettings,
  activeVoiceChannel,
  onJoinVoiceChannel,
  onLeaveVoiceChannel,
}) => {
  const { user, logout } = useAuth();
  const systemStatus = useSystemStatus();
  const voiceEnabled = systemStatus?.voice_enabled !== false;

  return (
    <div className="w-20 lg:w-72 border-r border-border flex flex-col bg-card/50 backdrop-blur-sm">
      {/* Logo */}
      <div className="h-16 flex items-center px-4 lg:px-5 border-b border-border gap-2">
        <ShieldCheck className="w-7 h-7 text-primary shrink-0" />
        <span
          className="hidden lg:flex flex-col text-[15px] font-bold leading-[1.05] tracking-tight"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          title="Government Shield"
        >
          <span>Government</span>
          <span>Shield</span>
        </span>
        <div className="ml-auto shrink-0">
          <LocalVerifiedBadge />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-1 py-4 flex flex-col">
        <nav className="space-y-1 px-2 lg:px-3">
          <button
            onClick={() => setActiveTab('chats')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
              activeTab === 'chats'
                ? 'bg-primary/10 text-primary'
                : 'text-slate-400 hover:bg-secondary hover:text-white'
            }`}
            data-testid="nav-chats"
          >
            <MessageSquare className="w-5 h-5" />
            <span className="hidden lg:block">Messages</span>
          </button>
          <button
            onClick={() => setActiveTab('contacts')}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 ${
              activeTab === 'contacts'
                ? 'bg-primary/10 text-primary'
                : 'text-slate-400 hover:bg-secondary hover:text-white'
            }`}
            data-testid="nav-contacts"
          >
            <Users className="w-5 h-5" />
            <span className="hidden lg:block">Contacts</span>
          </button>
        </nav>

        {/* Voice Channels Section — hidden if LiveKit isn't configured on the server */}
        {voiceEnabled && (
          <div className="hidden lg:block mt-4 border-t border-border pt-2">
            <VoiceChannelList onJoinChannel={onJoinVoiceChannel} />
          </div>
        )}
      </div>

      {/* Voice Status */}
      {voiceEnabled && activeVoiceChannel && (
        <VoiceStatusIndicator
          channel={activeVoiceChannel}
          onDisconnect={onLeaveVoiceChannel}
        />
      )}

      {/* Bottom Actions */}
      <div className="p-3 border-t border-border space-y-2">
        <div className="hidden lg:block">
          <DesktopDownloadButton variant="inline" />
        </div>
        <Button
          onClick={onNewChat}
          className="w-full hidden lg:flex items-center gap-2"
          data-testid="new-chat-btn"
        >
          <Plus className="w-4 h-4" />
          New Chat
        </Button>
        <Button
          onClick={onNewChat}
          size="icon"
          className="w-full lg:hidden"
          data-testid="new-chat-btn-mobile"
        >
          <Plus className="w-4 h-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-secondary transition-colors"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/20 text-primary">
                  {user?.username?.[0]?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="hidden lg:block text-left flex-1">
                <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={onSettings} data-testid="settings-menu-item">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} className="text-red-400" data-testid="logout-menu-item">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
